import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import { hotelDetailSelector, hotelMarkersSelector } from '@hotels/hotel-detail/store/selectors/hotel-detail.selector';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import {
  fetchHotelDetailAction, fetchHotelDetailDeeplinkAction, fetchHotelDetailSearchedAction,
  fetchHotelDetailPackageAction, fetchHotelDetailByCodePackageAction,
  setPackageBaseUrlResultInHotelDetailAction, setHotelResultUrlInHotelDetailAction, resetTooltipsAction
} from '@hotels/hotel-detail/store/actions/hotels.actions';
import { hotelSearchDetailSelector } from '@hotels/hotel-detail/store/selectors/hotel-search.selector';
import { HotelDetailPayload, HotelDetailDeepLinkPayload } from '@hotels/hotel-detail/entities/hotel-detail.payload';
import { MapMarkerInputs } from '@shared/map/constants/map.constants';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { hotelDetailSearchedSelector } from '@hotels/hotel-detail/store/selectors/hotel-detail-searched.selector';
import { HotelByCodePackagePayload, HotelDetailPackagePayload } from '@packages/packages-result/entities/hotel-detail';
import { AirResultOptionResponse } from '@packages/packages-result/entities/air-results';
import {
  savePackageSearchDataAction, setAirSelectedAction, setAirSelectedDesktopAction, setHotelRoomSelectedAction
} from '@packages/packages-result/store/actions/packages-result.actions';
import { FlightResultDesktop } from '@app/entities/flight-results';
import { PackageSeachResponse } from '@packages/packages-result/entities/packages-search';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { GlobalFacade } from '@app/global.facade';

@Injectable()
export class HotelDetailFacade {

  constructor(
    private store: Store<State>,
    private readonly globalFacade: GlobalFacade
  ) { }

  public hotelDetail$: Observable<HotelDetailResponse> = this.store.pipe(select(hotelDetailSelector), map(detail => detail));
  public errors$: Observable<ErrorHandle> = this.globalFacade.ui.errorHandle$;
  public isLoadingHotelDetail$: Observable<boolean> = this.globalFacade.ui.results.isLoadingResultsDetail$;
  public hotelMarkers$: Observable<MapMarkerInputs[]> = this.store.pipe(select(hotelMarkersSelector));
  public hotelSearchDetail$: Observable<HotelDetailPayload | HotelDetailDeepLinkPayload>
    = this.store.pipe(select(hotelSearchDetailSelector));
  public hotelDetailSearched$: Observable<HotelSearched>
    = this.store.pipe(select(hotelDetailSearchedSelector));

  public fetchHotelDetail(payload: HotelDetailPayload): void {
    this.store.dispatch(fetchHotelDetailAction({ payload }));
  }

  public fetchHotelDetailDeeplink(payload: HotelDetailDeepLinkPayload, isByhours: boolean): void {
    this.store.dispatch(fetchHotelDetailDeeplinkAction({ payload, isByhours }));
  }

  public fetchHotelDetailSearched(hotelSearched: HotelSearched): void {
    this.store.dispatch(fetchHotelDetailSearchedAction({ hotel: hotelSearched }));
  }

  public fetchHotelDetailPackage(payload: HotelDetailPackagePayload): void {
    return this.store.dispatch(fetchHotelDetailPackageAction({ payload }));
  }

  public fetchHotelDetailByCodePackage(payload: HotelByCodePackagePayload): void {
    return this.store.dispatch(fetchHotelDetailByCodePackageAction({ payload }));
  }

  public setAirSelected(payload: AirResultOptionResponse): void {
    this.store.dispatch(setAirSelectedAction({ payload }));
  }

  public setAirSelectedDesktop(payload: FlightResultDesktop): void {
    this.store.dispatch(setAirSelectedDesktopAction({ payload }));
  }

  public setHotelRoomSelected(id: string): void {
    this.store.dispatch(setHotelRoomSelectedAction({ id }));
  }

  public savePackageSearchData(response: PackageSeachResponse): void {
    this.store.dispatch(savePackageSearchDataAction({ response }));
  }

  public setPackageBaseUrlResultInHotelDetail(url: string): void {
    this.store.dispatch(setPackageBaseUrlResultInHotelDetailAction({ url }));
  }

  public setHotelResultUrlInHotelDetail(url: string): void {
    this.store.dispatch(setHotelResultUrlInHotelDetailAction({ url }));
  }

  public resetTooltips(payload: boolean = true): void {
    this.store.dispatch(resetTooltipsAction({payload}));
  }

}
